import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useOutletContext } from "react-router-dom";
import ResponseEvalDataTable from "./ResponseEvalDataTable";
import Pagination from "../../../components/Pagination/Pagination";
import ResponseFilterDropdown from "./ResponseFilterDropdown";
import ViewResponseModal from "./ViewResponseModal"; // IMPORT MODAL
import Cookies from "js-cookie";
import api from "../../../pages/chat/api";

const FILTER_CACHE_KEY = "response_eval_filters_v1";

const readCache = (key, fallback = null) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeCache = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

const ResponseEvaluationTab = () => {
  const { searchQuery, orgId, columnPreferences, isLoadingColumns } = useOutletContext();
  const [selectedRows, setSelectedRows] = useState([]);
  
  const [tableData, setTableData] = useState([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  
  // Modal State
  const [modalData, setModalData] = useState(null);

  const [currentFilters, setCurrentFilters] = useState(() => 
    readCache(FILTER_CACHE_KEY, { feedback: "All", contexts: [] })
  );

  useEffect(() => {
    writeCache(FILTER_CACHE_KEY, currentFilters);
  }, [currentFilters]);

  const [footerPortalNode, setFooterPortalNode] = useState(null);
  const [filterPortalNode, setFilterPortalNode] = useState(null);
  
  useEffect(() => {
    setFooterPortalNode(document.getElementById("analytics-footer-portal"));
    setFilterPortalNode(document.getElementById("analytics-filter-portal"));
  }, []);

  useEffect(() => {
    const fetchTableData = async () => {
      if (!orgId) {
        setTableData([]);
        return; 
      }

      setIsLoadingTable(true);
      try {
        const token = Cookies.get("access_token");
        const response = await api.get(`/api/v1/org/response-evaluation/?organization_id=${orgId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data && Array.isArray(response.data.results)) {
          const formattedData = response.data.results.map((item) => ({
            id: item.id,
            timestamp: item.timestamp 
              ? new Date(item.timestamp).toLocaleString("en-US", {
                  month: "2-digit", day: "2-digit", year: "numeric", 
                  hour: "2-digit", minute: "2-digit", hour12: true
                }).replace(",", "") 
              : "-", 
            feedback: item.feedback === "up" ? "Good" : item.feedback === "down" ? "Bad" : item.feedback,
            user_query: item.user_query || "-",
            response: item.response || "-",
            context: item.context || "-",
            member: item.member?.full_name 
              ? item.member.full_name.substring(0, 2).toUpperCase() 
              : String(item.member?.user_id || "U"),
            feedback_comment: item.feedback_comment || "No feedback"
          }));

          setTableData(formattedData);
        } else {
          setTableData([]);
        }
      } catch (error) {
        console.error("Error fetching table data:", error);
        setTableData([]);
      } finally {
        setIsLoadingTable(false);
      }
    };

    fetchTableData();
  }, [orgId]);

  const uniqueContexts = useMemo(() => {
    const contexts = new Set();
    tableData.forEach(item => {
      if (item.context && item.context !== "-") {
        contexts.add(item.context);
      }
    });
    return Array.from(contexts);
  }, [tableData]);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const filteredData = useMemo(() => {
    let result = tableData;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.user_query.toLowerCase().includes(query) || 
        item.feedback_comment.toLowerCase().includes(query)
      );
    }

    if (currentFilters.feedback !== "All") {
      result = result.filter(item => item.feedback === currentFilters.feedback);
    }

    if (currentFilters.contexts.length > 0) {
      result = result.filter(item => currentFilters.contexts.includes(item.context));
    }

    return result;
  }, [searchQuery, tableData, currentFilters]);

  const currentData = useMemo(() => {
    const indexOfLastItem = currentPage * rowsPerPage;
    const indexOfFirstItem = indexOfLastItem - rowsPerPage;
    return filteredData.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handleSelectAll = (isChecked, e) => {
    if (isChecked) {
      setSelectedRows(currentData.map(d => d.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => prev.includes(id) ? prev.filter(rId => rId !== id) : [...prev, id]);
  };

  const paginationUI = useMemo(() => {
    if (filteredData.length === 0) return null;
    return (
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredData.length}
        rowsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    );
  }, [filteredData.length, currentPage, totalPages, rowsPerPage]);

  const isCombinedLoading = isLoadingTable || isLoadingColumns;

  return (
    <>
      <div className="max-w-full overflow-x-hidden flex flex-col h-full bg-white pt-2">
        <div className="flex flex-col flex-1 overflow-hidden w-full pl-2 pr-1 pb-2">
          <div className="flex-1 flex flex-col w-[99%] ml-1 overflow-hidden bg-white border border-black/10 rounded-lg">
            <div className="flex-1 overflow-auto custom-scrollbar">
              <ResponseEvalDataTable 
                data={currentData} 
                loading={isCombinedLoading} 
                selectedRows={selectedRows}
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                columnPreferences={columnPreferences}
                onViewResponse={(item) => setModalData(item)} // PASS CLICK HANDLER
              />
            </div>
          </div>
        </div>
      </div>

      {filterPortalNode && createPortal(
        <ResponseFilterDropdown
          initialFilters={currentFilters}
          uniqueContexts={uniqueContexts}
          onApply={(filters) => {
            setCurrentFilters(filters);
            setCurrentPage(1); 
          }}
          onClear={() => {
            setCurrentFilters({ feedback: "All", contexts: [] });
            setCurrentPage(1); 
          }}
        />,
        filterPortalNode
      )}

      {footerPortalNode && createPortal(paginationUI, footerPortalNode)}

      <ViewResponseModal 
        isOpen={!!modalData} 
        onClose={() => setModalData(null)} 
        data={modalData} 
      />
    </>
  );
};

export default ResponseEvaluationTab;